import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { 
  Chat, 
  ArrowForward, 
  Refresh,
  Person,
  Schedule,
  Message,
  Settings
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';


// Types
interface Facility {
  id: number;
  name: string;
}

interface DriverMessage {
  threadUuid: string;
  shipmentId: string;
  shipmentNumber: string;
  driverName: string;
  driverPhone: string;
  lastMessage: string;
  lastActivity: string;
  messageCount: number;
  trailerNumber?: string;
  carrierName?: string;
  currentLocation?: string;
}

interface DriverCommsWidgetProps {
  selectedFacility: Facility | null;
  compactMode: boolean;
  data?: Record<string, any> | null;
  isRefreshing?: boolean;
  onRefresh?: () => void;
  onCommsSinceHoursChange?: (hours: number) => void;
  height?: number;
}

const DriverCommsWidget: React.FC<DriverCommsWidgetProps> = ({
  selectedFacility,
  compactMode,
  data: dashboardData,
  isRefreshing = false,
  onRefresh,
  onCommsSinceHoursChange,
  height
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // Widget configuration state
  const [sinceHours, setSinceHours] = useState<number>(() => {
    const saved = localStorage.getItem('driverCommsWidget_sinceHours');
    return saved ? parseInt(saved, 10) : 24;
  });
  const [showConfigMenu, setShowConfigMenu] = useState(false);
  const [configAnchorEl, setConfigAnchorEl] = useState<null | HTMLElement>(null);

  // Save sinceHours to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('driverCommsWidget_sinceHours', sinceHours.toString());
  }, [sinceHours]);

  // Process raw data from consolidated query
  const driverMessages: DriverMessage[] = React.useMemo(() => {
    const threads = dashboardData?.driverComms?.threads;
    if (!threads) return [];

    return threads
      .map((thread: any) => {
        const driver = thread.fromParticipant.driver;
        const driverName = driver
          ? `${driver.firstName || ''} ${driver.lastName || ''}`.trim() || 'Unknown Driver'
          : 'Unknown Driver';

        const shipment = thread.shipment;
        const trailer = shipment?.trailer;
        const referenceNumber = shipment?.referenceNumber || `Thread ${thread.uuid.substring(0, 8)}`;
        const trailerNumber = trailer?.trailerNumber || '';
        const carrierName = trailer?.carrier?.name || '';
        const currentLocation = trailer?.current_location?.name || '';

        return {
          threadUuid: thread.uuid,
          shipmentId: thread.fromParticipant.uuid,
          shipmentNumber: referenceNumber,
          driverName,
          driverPhone: driver?.phoneNumber || '',
          lastMessage: thread.message,
          lastActivity: thread.createdAt,
          messageCount: 1,
          trailerNumber,
          carrierName,
          currentLocation
        };
      })
      .sort((a: DriverMessage, b: DriverMessage) =>
        new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime()
      );
  }, [dashboardData?.driverComms]);

  const loading = !dashboardData && !error;

  // Navigate to driver communications page
  const handleViewAll = () => {
    navigate('/drivers/comms');
  };

  // Navigate to specific conversation with deep linking
  const handleViewConversation = (driverMessage: DriverMessage) => {
    console.log('Navigating to thread:', driverMessage.threadUuid);
    navigate(`/drivers/comms/${driverMessage.threadUuid}`);
  };

  // Format time display
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (date: Date): string => {
    const today = new Date();
    
    if (date.toDateString() === today.toDateString()) {
      return formatTime(date);
    }
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday ' + formatTime(date);
    }
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    }) + ' ' + formatTime(date);
  };

  // Get initials for avatar
  const getInitials = (name: string): string => {
    if (!name) return '';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  // Configuration menu handlers
  const handleConfigMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setConfigAnchorEl(event.currentTarget);
    setShowConfigMenu(true);
  };

  const handleConfigMenuClose = () => {
    setConfigAnchorEl(null);
    setShowConfigMenu(false);
  };

  const handleSinceHoursChange = (hours: number) => {
    setSinceHours(hours);
    onCommsSinceHoursChange?.(hours);
    handleConfigMenuClose();
  };

  // Get messages to display
  const displayMessages = driverMessages.slice(0, compactMode ? 3 : 5);

  return (
    <Paper sx={{ p: 3, height: height || (compactMode ? 320 : 400), display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chat color="primary" />
          <Typography variant="h6">
            Driver Messages
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {driverMessages.length > 0 && (
            <Chip 
              label={`${driverMessages.length} active`}
              color="info"
              size="small"
            />
          )}
          <Chip 
            label={`${sinceHours}h`}
            variant="outlined"
            size="small"
            sx={{ fontSize: '0.7rem' }}
          />
          <Tooltip title="Settings">
            <IconButton size="small" onClick={handleConfigMenuOpen}>
              <Settings fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={onRefresh} disabled={loading && !isRefreshing}>
              <Refresh fontSize="small" className={isRefreshing ? 'animate-spin' : ''} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 140 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="error" variant="body2">{error}</Typography>
          <Button size="small" onClick={onRefresh} sx={{ mt: 1 }}>
            Retry
          </Button>
        </Box>
      ) : displayMessages.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Message sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            No recent driver messages
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Messages from drivers will appear here
          </Typography>
        </Box>
      ) : (
        <>
          <List dense sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            {displayMessages.map((message, index) => (
              <React.Fragment key={message.threadUuid}>
                <ListItem
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderRadius: 1,
                    mb: 0.5,
                    transition: 'opacity 0.3s ease, transform 0.3s ease'
                  }}
                  onClick={() => handleViewConversation(message)}
                >
                  <ListItemAvatar>
                    <Badge 
                      color="primary" 
                      badgeContent={message.messageCount > 1 ? message.messageCount : 0}
                      invisible={message.messageCount <= 1}
                      overlap="circular"
                    >
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {getInitials(message.driverName)}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="medium" noWrap>
                          {message.driverName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(new Date(message.lastActivity))}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography 
                          variant="body2" 
                          color="text.primary" 
                          sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: compactMode ? 1 : 2,
                            WebkitBoxOrient: 'vertical',
                            mb: 0.5
                          }}
                        >
                          {message.lastMessage}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            label={message.shipmentNumber}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                          {message.trailerNumber && (
                            <Chip 
                              label={`Trailer: ${message.trailerNumber}`}
                              size="small"
                              variant="outlined"
                              color="secondary"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                          {message.carrierName && (
                            <Chip 
                              label={`Carrier: ${message.carrierName}`}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                          {message.currentLocation && (
                            <Chip 
                              label={`Location: ${message.currentLocation}`}
                              size="small"
                              variant="outlined"
                              color="info"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < displayMessages.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>

          {/* Footer Actions */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {driverMessages.length > displayMessages.length && (
              <Typography variant="caption" color="text.secondary">
                +{driverMessages.length - displayMessages.length} more conversations
              </Typography>
            )}
            <Button 
              size="small" 
              endIcon={<ArrowForward />}
              onClick={handleViewAll}
              sx={{ ml: 'auto' }}
            >
              View All
            </Button>
          </Box>
        </>
      )}

      {/* Configuration Menu */}
      <Menu
        anchorEl={configAnchorEl}
        open={showConfigMenu}
        onClose={handleConfigMenuClose}
        PaperProps={{
          sx: { minWidth: 180 }
        }}
      >
        <MenuItem disabled>
          <Typography variant="subtitle2" color="text.secondary">
            Time Range
          </Typography>
        </MenuItem>
        <MenuItem 
          onClick={() => handleSinceHoursChange(6)}
          selected={sinceHours === 6}
        >
          Last 6 hours
        </MenuItem>
        <MenuItem 
          onClick={() => handleSinceHoursChange(12)}
          selected={sinceHours === 12}
        >
          Last 12 hours
        </MenuItem>
        <MenuItem 
          onClick={() => handleSinceHoursChange(24)}
          selected={sinceHours === 24}
        >
          Last 24 hours
        </MenuItem>
        <MenuItem 
          onClick={() => handleSinceHoursChange(48)}
          selected={sinceHours === 48}
        >
          Last 48 hours
        </MenuItem>
        <MenuItem 
          onClick={() => handleSinceHoursChange(168)}
          selected={sinceHours === 168}
        >
          Last week
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default DriverCommsWidget;
